import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Trophy, Home, User, Sparkles, Lock, Search } from "lucide-react";

import { api } from "@/api/client";
import { useLang } from "@/context/LangContext";
import { Navbar } from "@/components/layout/Nav";
import { BadgePill, TierChip } from "@/components/Badges";
import { Mascot } from "@/components/characters/Characters";

const PersonalCard = ({ me, lang }) => {
  if (!me) return null;
  if (!me.found) {
    return (
      <div className="brutal p-5 bg-canales-cream mt-6" data-testid="ranking-me-not-found">
        <div className="font-display font-black text-lg">
          {lang === "es" ? "No encontramos exámenes con ese nombre" : "No exams found for that name"}
        </div>
        <p className="font-body text-sm mt-1">
          {lang === "es"
            ? "Verifica que sea el mismo nombre que usaste al iniciar el examen."
            : "Make sure it's the same name you used to start the exam."}
        </p>
      </div>
    );
  }
  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className="brutal p-5 bg-canales-yellow mt-6"
      data-testid="ranking-me-card"
    >
      <div className="flex items-center gap-3 flex-wrap">
        <div className="w-14 h-14 rounded-2xl border-2 border-black bg-white flex items-center justify-center">
          <User className="w-7 h-7" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <div className="font-display font-black text-2xl truncate">{me.employee_name}</div>
            <TierChip tier={me.tier} lang={lang} />
          </div>
          <div className="mt-1 flex flex-wrap gap-1.5">
            {me.badges.map((b) => (
              <BadgePill key={b.code} badge={b} lang={lang} size="sm" />
            ))}
          </div>
        </div>
        <div className="text-right">
          <div className="text-xs font-display font-bold uppercase tracking-widest">
            {lang === "es" ? "Tu promedio" : "Your avg"}
          </div>
          <div className="font-display font-black text-3xl">{me.avg}%</div>
        </div>
      </div>

      <div className="mt-4 grid grid-cols-3 gap-2">
        <div className="brutal-sm p-2 bg-white text-center">
          <div className="text-xs font-display font-bold uppercase tracking-widest text-neutral-600">
            {lang === "es" ? "Mejor" : "Best"}
          </div>
          <div className="font-display font-black text-xl">{me.best}%</div>
        </div>
        <div className="brutal-sm p-2 bg-white text-center">
          <div className="text-xs font-display font-bold uppercase tracking-widest text-neutral-600">
            {lang === "es" ? "Aprobados" : "Passed"}
          </div>
          <div className="font-display font-black text-xl">{me.passed}</div>
        </div>
        <div className="brutal-sm p-2 bg-white text-center">
          <div className="text-xs font-display font-bold uppercase tracking-widest text-neutral-600">
            {lang === "es" ? "Intentos" : "Attempts"}
          </div>
          <div className="font-display font-black text-xl">{me.attempts}</div>
        </div>
      </div>

      {me.history?.length > 0 && (
        <div className="mt-4">
          <div className="text-xs font-display font-bold uppercase tracking-widest mb-2">
            {lang === "es" ? "Tu historial" : "Your history"}
          </div>
          <ul className="space-y-1 max-h-56 overflow-auto">
            {me.history.map((h) => (
              <li
                key={h.attempt_id}
                className="brutal-sm p-2 bg-white text-sm font-body flex justify-between gap-2"
              >
                <span className="truncate">{lang === "es" ? h.exam_title_es : h.exam_title_en}</span>
                <span className={`font-display font-bold ${h.passed ? "text-green-700" : "text-red-700"}`}>
                  {h.score_percent}%
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </motion.div>
  );
};

export default function Ranking() {
  const { t, lang } = useLang();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [name, setName] = useState(() => localStorage.getItem("canales_employee") || "");
  const [inputName, setInputName] = useState(name);
  const [me, setMe] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/public/ranking");
        setRows(data);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  useEffect(() => {
    if (!name) return;
    (async () => {
      const { data } = await api.get(`/public/employee/${encodeURIComponent(name)}/summary`);
      setMe(data);
    })();
  }, [name]);

  const submitName = (e) => {
    e.preventDefault();
    const clean = inputName.trim();
    if (!clean) return;
    localStorage.setItem("canales_employee", clean);
    setName(clean);
  };

  return (
    <div className="min-h-screen bg-halftone">
      <Navbar />
      <div className="max-w-5xl mx-auto px-4 py-10" data-testid="ranking-root">
        <div className="brutal p-6 bg-canales-yellow flex items-center gap-4 flex-wrap">
          <Trophy className="w-10 h-10" />
          <div className="flex-1">
            <h1 className="font-display font-black text-3xl sm:text-4xl">{t("ranking")}</h1>
            <p className="font-body">
              {lang === "es"
                ? "Los mejores vendedores Canales. Tu información personal es privada — solo tú (con tu nombre) y el admin pueden verla."
                : "Top Canales sellers. Your personal metrics stay private — only you (with your name) and the admin can view them."}
            </p>
          </div>
        </div>

        {/* Personal unlock */}
        <form onSubmit={submitName} className="mt-4 brutal p-4 bg-white flex flex-wrap items-center gap-2">
          <Search className="w-4 h-4 text-neutral-500" />
          <label className="font-display font-bold text-sm">
            {lang === "es" ? "Ver mis datos:" : "See my data:"}
          </label>
          <input
            data-testid="ranking-name-input"
            value={inputName}
            onChange={(e) => setInputName(e.target.value)}
            placeholder={lang === "es" ? "Tu nombre completo" : "Your full name"}
            className="brutal-sm px-3 py-2 font-body flex-1 min-w-[160px]"
          />
          <button
            type="submit"
            data-testid="ranking-name-submit"
            className="brutal-btn bg-canales-red text-white px-4 py-2 font-display font-bold text-sm"
          >
            {lang === "es" ? "Desbloquear" : "Unlock"}
          </button>
        </form>

        <PersonalCard me={me} lang={lang} />

        {loading ? (
          <div className="mt-8 font-display">Loading…</div>
        ) : rows.length === 0 ? (
          <div className="mt-8 brutal p-8 text-center bg-white">
            <Mascot size={100} />
            <p className="mt-4 font-body text-lg">
              {lang === "es"
                ? "Aún no hay vendedores en el ranking. ¡Sé el primero en tomar un examen!"
                : "No sellers yet on the leaderboard. Be the first to take an exam!"}
            </p>
            <Link to="/exams-info" className="mt-4 inline-flex items-center gap-2 brutal-btn bg-canales-blue text-white px-4 py-2">
              {t("exams")}
            </Link>
          </div>
        ) : (
          <div className="mt-8">
            <div className="flex items-center gap-2 mb-3 text-sm font-body text-neutral-600">
              <Lock className="w-4 h-4" />
              {lang === "es"
                ? "Métricas personales ocultas para proteger la privacidad de cada vendedor."
                : "Personal metrics hidden to protect each seller's privacy."}
            </div>
            <div className="grid gap-3">
              {rows.map((r, i) => {
                const podium = i < 3;
                const podiumColor = i === 0 ? "#FFD100" : i === 1 ? "#C0C0C0" : i === 2 ? "#CD7F32" : "#fff";
                const isMe = name && r.employee_name.toLowerCase() === name.toLowerCase();
                return (
                  <motion.div
                    key={r.employee_name}
                    initial={{ opacity: 0, x: -10 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: i * 0.03 }}
                    data-testid={`ranking-row-${i + 1}`}
                    className={`brutal p-4 flex items-center gap-4 flex-wrap ${
                      isMe ? "bg-canales-yellow ring-4 ring-canales-red" : "bg-white"
                    }`}
                  >
                    <div
                      className="w-14 h-14 rounded-2xl border-2 border-black flex items-center justify-center font-display font-black text-2xl shrink-0"
                      style={{ background: podium ? podiumColor : "#fff" }}
                    >
                      #{i + 1}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <h3 className="font-display font-black text-xl truncate">{r.employee_name}</h3>
                        <TierChip tier={r.tier} lang={lang} />
                        {isMe && (
                          <span className="brutal-sm bg-canales-red text-white px-2 py-0.5 text-xs font-display font-bold">
                            {lang === "es" ? "TÚ" : "YOU"}
                          </span>
                        )}
                      </div>
                      <div className="mt-1 flex flex-wrap gap-1.5">
                        {r.badges.map((b) => (
                          <BadgePill key={b.code} badge={b} lang={lang} size="sm" />
                        ))}
                      </div>
                    </div>
                    {isMe && me?.found && (
                      <div className="brutal-sm bg-white px-3 py-1 font-display font-bold text-sm">
                        <Sparkles className="w-3 h-3 inline mr-1" />
                        {me.avg}%
                      </div>
                    )}
                  </motion.div>
                );
              })}
            </div>
          </div>
        )}

        <div className="mt-10 flex justify-center">
          <Link to="/" className="brutal-btn bg-white text-black px-4 py-2 flex items-center gap-2">
            <Home className="w-4 h-4" /> {lang === "es" ? "Volver al inicio" : "Back home"}
          </Link>
        </div>
      </div>
    </div>
  );
}
